import React from 'react';
import { motion } from 'framer-motion';
export interface FractionStripProps {
  /** Array of fractions to display as strips for comparison */
  fractions: Array<{
    numerator: number;
    denominator: number;
    label?: string;
  }>;
  /** Optional label above the strips */
  label?: string;
  /** Highlight a specific fraction index */
  highlightIndex?: number;
  /** 'stimulus' = compact, 'support' = educational panel */
  mode?: 'stimulus' | 'support';
}
function isFiniteNumber(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}
const STRIP_COLORS = [{
  bg: 'bg-blue-500',
  light: 'bg-blue-100',
  border: 'border-blue-300',
  text: 'text-blue-800'
}, {
  bg: 'bg-emerald-500',
  light: 'bg-emerald-100',
  border: 'border-emerald-300',
  text: 'text-emerald-800'
}, {
  bg: 'bg-amber-500',
  light: 'bg-amber-100',
  border: 'border-amber-300',
  text: 'text-amber-800'
}, {
  bg: 'bg-purple-500',
  light: 'bg-purple-100',
  border: 'border-purple-300',
  text: 'text-purple-800'
}, {
  bg: 'bg-rose-500',
  light: 'bg-rose-100',
  border: 'border-rose-300',
  text: 'text-rose-800'
}];
function FractionLabel({
  numerator,
  denominator
}: {
  numerator: number;
  denominator: number;
}) {
  return <span className="inline-flex flex-col items-center leading-none mx-1" data-id="element-13420" data-mp="3bgrbusoz3653">
      <span className="text-xs font-bold border-b border-current px-0.5" data-id="element-13421" data-mp="36ipqu27zkwqz">
        {numerator}
      </span>
      <span className="text-xs font-bold px-0.5" data-id="element-13422" data-mp="31lzcpkfqsb87">{denominator}</span>
    </span>;
}
function Strip({
  numerator,
  denominator,
  label,
  colorIdx,
  isHighlighted,
  delay,
  mode
}: {
  numerator: number;
  denominator: number;
  label?: string;
  colorIdx: number;
  isHighlighted: boolean;
  delay: number;
  mode: 'stimulus' | 'support';
}) {
  const colors = STRIP_COLORS[colorIdx % STRIP_COLORS.length];
  const fillPercent = denominator > 0 ? numerator / denominator * 100 : 0;
  const clampedFill = Math.max(0, Math.min(100, fillPercent));
  return <div className={`space-y-1 ${isHighlighted ? 'ring-2 ring-indigo-400 ring-offset-2 rounded-lg p-1' : ''}`} data-id="element-13423" data-mp="2g9s0jjsgri5k">
      
      {/* Label */}
      <div className="flex items-center justify-between" data-id="element-13424" data-mp="2l10bik9fzbku">
        <div className={`text-xs font-semibold ${colors.text} flex items-center`} data-id="element-13425" data-mp="26bkonfd17qaf">
          
          {label || <span className="flex items-center gap-1" data-id="element-13426" data-mp="2b9vwxlnohsqj">
              <FractionLabel numerator={numerator} denominator={denominator} data-id="element-13427" data-mp="2baozeafltw2v" />
            </span>}
        </div>
        {mode !== 'stimulus' && <span className="text-[10px] text-slate-400" data-id="element-13428" data-mp="33yt4ouu9k751">
            {Math.round(clampedFill)}%
          </span>}
      </div>

      {/* Strip bar */}
      <div className={`relative h-7 ${colors.light} rounded-md overflow-hidden border ${colors.border}`} data-id="element-13429" data-mp="2p1ahhwx8i1zp">
        
        {/* Division lines */}
        {Array.from({
        length: denominator - 1
      }, (_, i) => <div key={i} className="absolute inset-y-0 w-px bg-slate-400/40 z-10" style={{
        left: `${(i + 1) / denominator * 100}%`
      }} data-id="element-13430" data-mp="1dlc3rfr42rs5" />)}

        {/* Filled portion */}
        <motion.div initial={{
        width: 0
      }} animate={{
        width: `${clampedFill}%`
      }} transition={{
        delay,
        duration: 0.5,
        ease: 'easeOut'
      }} className={`absolute inset-y-0 left-0 ${colors.bg} rounded-l-md`} data-id="element-13431" data-mp="mtoyg2dcrqh" />
        
      </div>
    </div>;
}
export function FractionStrip({
  fractions,
  label,
  highlightIndex,
  mode = 'support'
}: FractionStripProps) {
  const validFractions = (fractions || []).filter(f => isFiniteNumber(f.numerator) && isFiniteNumber(f.denominator) && f.denominator > 0);
  if (validFractions.length === 0) {
    return <div className="bg-red-50 p-4 rounded-xl border border-red-200 text-center" data-id="element-13432" data-mp="3mw4qkk51bnzo">
        <p className="text-red-600 text-sm font-medium" data-id="element-13433" data-mp="3i4cvpxeuiw3q">
          Ongeldige breukenstrook configuratie
        </p>
      </div>;
  }
  // Always show the "whole" (1/1) reference strip
  const showReference = validFractions.every(f => f.denominator > 1);
  const strips = <div className="space-y-3" data-id="element-13434" data-mp="332o8j6eh0bjs">
      {/* Reference strip: 1 geheel */}
      {showReference && <Strip numerator={1} denominator={1} label="1 geheel" colorIdx={-1} isHighlighted={false} delay={0} mode={mode} data-id="element-13435" data-mp="330tl9lfq87cj" />}

      {/* Fraction strips */}
      {validFractions.map((f, i) => <Strip key={i} numerator={f.numerator} denominator={f.denominator} label={f.label} colorIdx={i} isHighlighted={highlightIndex === i} delay={(i + 1) * 0.15} mode={mode} data-id="element-13436" data-mp="3czj71paquby2" data-mpi={i} />)}
    </div>;

  // Stimulus mode: compact
  if (mode === 'stimulus') {
    return <div className="bg-blue-50 rounded-xl px-5 py-4 border border-blue-200" data-id="element-13437" data-mp="3qrpiiy2ybur6">
        {label && <p className="text-sm font-semibold text-blue-800 mb-3 text-center" data-id="element-13438" data-mp="3vqtu1vdcm4ko">
            {label}
          </p>}
        {strips}
      </div>;
  }
  // Support mode: educational panel
  return <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-2xl border-2 border-blue-200 shadow-sm" data-id="element-13439" data-mp="21darg74gnvsi">
      {label && <div className="mb-5 text-center" data-id="element-13440" data-mp="26bm0wgqug4a7">
          <div className="inline-flex items-center gap-2 bg-white/90 rounded-xl px-4 py-2 border border-blue-200 shadow-sm" data-id="element-13441" data-mp="26ehxa0n1mkwj">
            <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" data-id="element-13442" data-mp="1u264ut7dyv5j" />
            <span className="text-sm font-bold text-blue-900" data-id="element-13443" data-mp="1u034bf8lxjmk">{label}</span>
          </div>
        </div>}

      <div className="mb-5" data-id="element-13444" data-mp="1p0qi8wpk8gl1">{strips}</div>

      {/* Educational explanation */}
      {validFractions.length >= 2 && <div className="bg-white/80 rounded-xl p-4 border border-blue-100 shadow-sm" data-id="element-13445" data-mp="2hrqm97nw5kcp">
          <div className="flex items-start gap-3" data-id="element-13446" data-mp="2hpe9d8wzq06i">
            <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-13447" data-mp="2ctp7gyxi0771">
              <span className="text-emerald-600 text-sm" data-id="element-13448" data-mp="27rzs5738t7d5">💡</span>
            </div>
            <div data-id="element-13449" data-mp="22tg676onfvks">
              <h4 className="text-sm font-bold text-emerald-900 mb-1" data-id="element-13450" data-mp="22ty8ajxzt40m">
                Vergelijk de stroken
              </h4>
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-13451" data-mp="wc1e0ot7jv52">
                Kijk naar de gekleurde delen. Hoe meer gekleurd, hoe groter de
                breuk.
                {validFractions.length === 2 && (() => {
              const a = validFractions[0].numerator / validFractions[0].denominator;
              const b = validFractions[1].numerator / validFractions[1].denominator;
              if (Math.abs(a - b) < 0.001) {
                return ' Deze breuken zijn gelijk!';
              }
              return a > b ? ' De bovenste breuk is groter.' : ' De onderste breuk is groter.';
            })()}
              </p>
            </div>
          </div>
        </div>}
    </div>;
}